import * as React from 'react';
import { ISeedProduct, ISeedAttributeValue, IDocument } from '../../../infrastructure/interfaces';
import MarkdownRenderer from '../../Editor/components/CropPageView/common/MarkdownRenderer';
import { SeedCategory } from 'src/infrastructure/enums';
import './TypeSeedHeader.css';

export interface ITypeSeedHeader {
  product: ISeedProduct;
  document?: IDocument;
}

export default class TypeSeedHeader extends React.Component<ITypeSeedHeader> {

  render () {
    const {
      name: productName,
      category: {
        name: categoryName,
        parentName: categoryParentName,
      }
    } = this.props.product;
    const isNew = this.props.product.isNew;
    const classes = ['product-head'];
    const subheaderLeft = this.getSubheaderLeft();
    const subheaderCenter = this.getSubheaderCenter();
    const subheaderRight = this.getSubheaderRight();

    const docType = this.props.document ? this.props.document.type : "";

    if (isNew) {
      classes.push('is-new');
    }

    if (this.isDarkTheme()) {
      classes.push('dark-theme');
    }

    return (
      <div className={`${docType} ${classes.join(' ')}`}>
        <div className={`${docType} head-wrapper`}>
          {isNew && <div className="new-flag"><div>NEW</div></div>}
          <div className={`${docType} top-description`}>
            {this.getTopDescription()}
          </div>
          <div className={`${docType} category-name`}>
            {categoryParentName || categoryName}
          </div>
          <div className={`${docType}  product-name`}>
            <MarkdownRenderer markdown={productName} />
            <div className={`${docType} brand-name`}>
              {this.getBrand()}
            </div>
          </div>
        </div>
        <div className={`${docType} subheader`}>
          {subheaderLeft && <div className="subheader-left">
            {subheaderLeft}
          </div>}
          {subheaderCenter && <div className="subheader-center">
            {subheaderCenter}
          </div>}
          {subheaderRight && <div className="subheader-right">
            {subheaderRight}
          </div>}
        </div>
      </div>
    );
  }

  getBrand(): string {
    const {
      category: {
        id: categoryId,
      },
      brand,
    } = this.props.product;
    switch (categoryId) {
      // Wheat
      case SeedCategory.HardRedWinterWheat:
        return 'Hard Red Winter';
      case SeedCategory.HardRedSpringWheat:
        return 'Hard Red Spring';
      case SeedCategory.SoftRedWinterWheat:
        return 'Soft Red Winter';
      case SeedCategory.SoftWhiteWinterWheat:
        return 'Soft White Winter';
      case SeedCategory.DurumWheat:
        return 'Amber Durum';
      // Everything else
      default:
        return brand ? 'Brand' : '';
    }
  }

  getTopDescription(): string {
    const {
      category: {
        id: categoryId,
      }
    } = this.props.product;
    let value: string;
    switch (categoryId) {
      case SeedCategory.Alfalfa:
        value = this.attributeValue('FallDormancy');
        return value !== '' && `FD ${value}` || '';
      case SeedCategory.Corn:
      case SeedCategory.Silage:
      case SeedCategory.SoybeansOver3_5RM:
      case SeedCategory.SoybeansUnder3_5RM:
        value = this.attributeValue('RelativeMaturity');
        return value !== '' && `${value} RM` || '';
      case SeedCategory.Cotton:
      case SeedCategory.SoftRedWinterWheat:
      case SeedCategory.GrainSorghum:
      case SeedCategory.HardRedSpringWheat:
      case SeedCategory.HardRedWinterWheat:
      case SeedCategory.SoftWhiteWinterWheat:
        return `${this.attributeValue('RelativeMaturity')}`;
      case SeedCategory.ForageSorghum:
      case SeedCategory.SudangrassSorghum:
        return `${this.attributeValue('Maturity')}`;
      case SeedCategory.Sunflower:
        return `${this.attributeValue('MaturityGroup')}`;
      default:
        return '';
    }
  }

  getSubheaderLeft(): string {
    const {
      category: {
        id: categoryId,
        name: categoryName,
      }
    } = this.props.product;
    switch (categoryId) {
      case SeedCategory.Sunflower:
        return `${this.attributeValue('Oil')}`;
      case SeedCategory.ForageSorghum:
      case SeedCategory.GrainSorghum:
      case SeedCategory.SudangrassSorghum:
        return `${categoryName}`;
      default:
        return '';
    }
  }

  getSubheaderCenter(): string {
    const {
      category: {
        id: categoryId,
      }
    } = this.props.product;
    let value: string;
    switch (categoryId) {
      case SeedCategory.Corn:
      case SeedCategory.Silage:
        value = this.attributeValue('AvailableRib');
        return value !== '' && `${this.attributeName('AvailableRib')}: ${value}` || '';
      default:
        return '';
    }
  }

  getSubheaderRight(): string {
    const {
      category: {
        id: categoryId,
      }
    } = this.props.product;
    let value: string;
    switch (categoryId) {
      case SeedCategory.Sunflower:
        return `${this.attributeValue('MaturityRange')}`;
      case SeedCategory.ForageSorghum:
      case SeedCategory.SudangrassSorghum:
        value = this.attributeValue('DaysToSoftDough');
        return value !== '' && `${this.attributeName('DaysToSoftDough')}: ${value}` || '';
      case SeedCategory.GrainSorghum:
        value = this.attributeValue('DaysMidbloom');
        return value !== '' && `${this.attributeName('DaysMidbloom')}: ${value}` || '';
      default:
        return '';
    }
  }

  attributeValue(name: string|string[], delimiter: string = ''): string {
    const {
      attributes,
    } = this.props.product;
    const value: string[] = [];
    const names: string[] = typeof name === 'string' ? [name] : name;
    attributes.forEach((attrData: ISeedAttributeValue) => {
      if (names.includes(attrData.attribute.name)) {
        value.push(attrData.value);
      }
    });
    return value.join(delimiter);
  }

  attributeName(name: string|string[]): string {
    const {
      attributes,
    } = this.props.product;
    let title = '';
    const findName: string = typeof name === 'string' ? name : name[0];
    attributes.forEach((attrData: ISeedAttributeValue) => {
      if (attrData.attribute.name === findName) {
        title = attrData.attribute.productDescriptionDisplayName;
      }
    });
    return title;
  }

  isDarkTheme(): boolean {
    const {
      category: {
        id: categoryId,
      }
    } = this.props.product;
    switch (categoryId) {
      case SeedCategory.Alfalfa:
      case SeedCategory.Canola:
      case SeedCategory.Corn:
      case SeedCategory.Cotton:
      case SeedCategory.Rice:
      case SeedCategory.Silage:
      case SeedCategory.ForageSorghum:
      case SeedCategory.GrainSorghum:
      case SeedCategory.SudangrassSorghum:
      case SeedCategory.SoybeansOver3_5RM:
      case SeedCategory.SoybeansUnder3_5RM:
      case SeedCategory.Barley:
        return true;
      default:
        return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/Products/components/TypeSeedHeader.tsx